/**
 * Medical Record Service
 * API integration for medical consultation operations
 *
 * @version 2025.1.0
 * @description Service for managing medical consultations and records
 */

import apiClient from './apiClient';
import {
  MedicalRecord,
  MedicalRecordFilters,
  ConsultationQueueFilters,
  PaginationParams,
  ConsultationQueueItem,
  StartConsultationData,
  UpdateMedicalRecordData,
  CompleteConsultationData,
  EmployeeMedicalHistory,
  MedicalRecordStatistics,
} from '../types/medicalRecord';
import { ApiResponse, PaginatedResponse, isApiError } from '../types/common';

// Re-export types for convenience
export * from '../types/medicalRecord';

/**
 * Helper to extract data from API response
 */
const extractData = <T>(response: ApiResponse<T>): T => {
  return response.data as T;
};

class MedicalRecordService {
  private baseUrl = '/medical-records';

  // ============================================================================
  // CONSULTATION QUEUE
  // ============================================================================

  /**
   * Get consultation queue (appointments ready for medical consultation)
   */
  async getConsultationQueue(
    params: ConsultationQueueFilters & PaginationParams = {}
  ): Promise<PaginatedResponse<ConsultationQueueItem>> {
    const {
      page = 1,
      limit = 50,
      sortBy = 'queueNumber',
      sortOrder = 'ASC',
      ...filters
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder,
    });

    // Add filters
    if (filters.date) queryParams.append('date', filters.date);
    if (filters.clinicId) queryParams.append('clinicId', filters.clinicId.toString());
    if (filters.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.examType) queryParams.append('examType', filters.examType);

    // Handle array filter (attendanceStatus)
    if (filters.attendanceStatus) {
      const statuses = Array.isArray(filters.attendanceStatus)
        ? filters.attendanceStatus
        : [filters.attendanceStatus];
      statuses.forEach((s) => queryParams.append('attendanceStatus', s));
    }

     
    const response = await apiClient.get<ConsultationQueueItem[]>(
      `${this.baseUrl}/queue?${queryParams.toString()}`
    );

    const data = response.data ?? [];
    const pag = response.pagination;

    return {
      data,
      pagination: {
        total: (pag?.total || data.length) ?? 0,
        page: pag?.page || page,
        limit: pag?.limit || limit,
        totalPages:
          pag?.totalPages || Math.ceil((pag?.total || data.length) / limit) || 0,
      },
    };
  }

  // ============================================================================
  // MEDICAL RECORDS CRUD
  // ============================================================================

  /**
   * Get all medical records with filters
   */
  async getMedicalRecords(
    params: MedicalRecordFilters & PaginationParams = {}
  ): Promise<PaginatedResponse<MedicalRecord>> {
    const {
      page = 1,
      limit = 20,
      sortBy = 'consultationStartedAt',
      sortOrder = 'DESC',
      ...filters
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      sortBy,
      sortOrder,
    });

    // Add filters
    if (filters.employeeId) queryParams.append('employeeId', filters.employeeId.toString());
    if (filters.professionalId)
      queryParams.append('professionalId', filters.professionalId.toString());
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters.clinicId) queryParams.append('clinicId', filters.clinicId.toString());

    // Handle array filters
    if (filters.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
      statuses.forEach((s) => queryParams.append('status', s));
    }
    if (filters.medicalOpinion) {
      const opinions = Array.isArray(filters.medicalOpinion)
        ? filters.medicalOpinion
        : [filters.medicalOpinion];
      opinions.forEach((o) => queryParams.append('medicalOpinion', o));
    }

    const response = await apiClient.get<MedicalRecord[]>(
      `${this.baseUrl}?${queryParams.toString()}`
    );

    const data = response.data ?? [];
    const pag = response.pagination;

    return {
      data,
      pagination: {
        total: (pag?.total || data.length) ?? 0,
        page: pag?.page || page,
        limit: pag?.limit || limit,
        totalPages:
          pag?.totalPages || Math.ceil((pag?.total || data.length) / limit) || 0,
      },
    };
  }

  /**
   * Get medical record by ID
   */
  async getMedicalRecord(id: number): Promise<MedicalRecord> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return extractData(response);
  }

  /**
   * Get medical record by appointment ID
   */
  async getMedicalRecordByAppointment(appointmentId: number): Promise<MedicalRecord | null> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/appointment/${appointmentId}`);
      return extractData(response);
    } catch (error) {
      // Return null if not found (404)
      // httpClient transforms errors to ApiError { status, message, ... }
      if (isApiError(error) && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get employee medical history
   */
  async getEmployeeHistory(
    employeeId: number,
    params: PaginationParams = {}
  ): Promise<EmployeeMedicalHistory> {
    const { page = 1, limit = 10 } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    const response = await apiClient.get(
      `${this.baseUrl}/employee/${employeeId}/history?${queryParams.toString()}`
    );
    return extractData(response);
  }

  // ============================================================================
  // CONSULTATION FLOW
  // ============================================================================

  /**
   * Start a new medical consultation
   */
  async startConsultation(data: StartConsultationData): Promise<MedicalRecord> {
    const response = await apiClient.post(this.baseUrl, data);
    return extractData(response);
  }

  /**
   * Update medical record (partial update)
   */
  async updateMedicalRecord(id: number, data: UpdateMedicalRecordData): Promise<MedicalRecord> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return extractData(response);
  }

  /**
   * Complete medical consultation
   */
  async completeConsultation(id: number, data: CompleteConsultationData): Promise<MedicalRecord> {
    const response = await apiClient.put(`${this.baseUrl}/${id}/complete`, data);
    return extractData(response);
  }

  // ============================================================================
  // ASO GENERATION
  // ============================================================================

  /**
   * Generate ASO PDF
   */
  async generateAsoPdf(id: number): Promise<{ pdfPath: string }> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/generate-aso`);
    return extractData(response);
  }

  /**
   * Generate Guide PDF (Guia de Atendimento)
   * @param appointmentId - ID of the appointment
   */
  async generateGuidePdf(appointmentId: number): Promise<{ pdfPath: string }> {
    const response = await apiClient.post(`${this.baseUrl}/${appointmentId}/generate-guide`);
    return extractData(response);
  }

  /**
   * Generate Medical Record PDF (Prontuário)
   * @param id - ID of the medical record
   */
  async generateProntuarioPdf(id: number): Promise<{ pdfPath: string }> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/generate-prontuario`);
    return extractData(response);
  }

  // ============================================================================
  // CALL PATIENT (Queue Management)
  // ============================================================================

  /**
   * Call a patient (update queueCalledAt timestamp for TV display panel)
   */
  async callPatient(appointmentId: number): Promise<void> {
    await apiClient.post(`/appointments/${appointmentId}/call`);
  }

  // ============================================================================
  // STATISTICS
  // ============================================================================

  /**
   * Get statistics
   */
  async getStatistics(params: MedicalRecordFilters = {}): Promise<MedicalRecordStatistics> {
    const queryParams = new URLSearchParams();

    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.companyId) queryParams.append('companyId', params.companyId.toString());
    if (params.clinicId) queryParams.append('clinicId', params.clinicId.toString());

    const response = await apiClient.get(`${this.baseUrl}/statistics?${queryParams.toString()}`);
    return extractData(response);
  }

  // ============================================================================
  // HEALTH CHECK
  // ============================================================================

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; module: string; timestamp: string }> {
    const response = await apiClient.get<{ status: string; module: string; timestamp: string }>(
      `${this.baseUrl}/health`
    );
    return response.data || { status: 'unknown', module: 'medical-records', timestamp: new Date().toISOString() };
  }
}

// Singleton export
export const medicalRecordService = new MedicalRecordService();
export default medicalRecordService;
